// Vendor Libs
import {
    cloneDeep,
    compact,
    find,
    findIndex,
    first,
    get,
    includes,
    intersection,
    isArray,
    isEmpty,
    isPlainObject,
    isString,
    join,
    map,
    now,
    omit,
    pick,
    pickBy,
    reduce,
    set,
    uniq,
} from 'lodash';
import moment from 'moment';
import {push} from 'redux-first-history';

// Lib App
import actionsServiceCaller, {generateCancelToken} from '../lib/app/actions-service-caller';

// Configs
import servicesConfig from '../../config/services-config';
import rsqlConfig from '../../config/rsql-config';

const currentCancelTokens = {};

const fetchMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    const state = getState();

    next(action);

    const nextState = getState();
    const actions = {
        SEARCH_DATA_REGISTER_STORE: () => storeRegistered(dispatch, getState, action.servicesDataKey, nextState),
        SEARCH_DATA_REFRESH: () =>
            refreshServicesData(dispatch, action.servicesDataKey, nextState, action.selectRecord),
        SEARCH_DATA_SEARCH: () => fetchServicesData(dispatch, action.servicesDataKey, nextState, action.selectRecord),
        SEARCH_DATA_SET_QUERY: () => fetchServicesData(dispatch, action.servicesDataKey, nextState),
        SEARCH_DATA_SORT: () => fetchServicesData(dispatch, action.servicesDataKey, nextState),
        SEARCH_DATA_FETCH_FULFILLED: () => {
            if (action.selectRecord) {
                selectRecord(dispatch, action.servicesDataKey, state, nextState);
            }

            return Promise.all([
                fetchDataForConsumers(dispatch, action.servicesDataKey, nextState),
                fetchDataForSearch(dispatch, action.servicesDataKey),
            ]);
        },
        SEARCH_DATA_RESET: () => {
            if (action.actionShouldFetch) {
                return fetchServicesData(dispatch, action.servicesDataKey, nextState);
            }
        },
        SEARCH_DATA_SORT_RESET: () => {
            if (action.actionShouldFetch) {
                return fetchServicesData(dispatch, action.servicesDataKey, nextState);
            }
        },
        FETCH_NEXT_ITEM: () => {
            selectRecord(dispatch, action.servicesDataKey, state, nextState);
        },
        FETCH_PREVIOUS_ITEM: () => {
            selectRecord(dispatch, action.servicesDataKey, state, nextState, true);
        },
        ROUTER_UPDATE: () => handleRouterUpdate(dispatch, action.routerParams, state, nextState),
        AUTHENTICATE_LOGOUT: () => resetServicesData(dispatch),
    };

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function selectRecord(dispatch, servicesDataKey, state, nextState, reverse) {
    const {activeRowKeys, route} = servicesConfig[servicesDataKey];

    if (activeRowKeys && route) {
        const searchData = get(state.servicesSearchData, `${servicesDataKey}.searchData.content`, []);
        const nextSearchData = get(nextState.servicesSearchData, `${servicesDataKey}.searchData.content`, []);

        if (searchData.length || nextSearchData.length) {
            const routerParams = get(nextState.router, 'routerParams', {});
            let routeItem;

            if (!isEmpty(routerParams)) {
                const currentIndex = findIndex(searchData, pick(routerParams, activeRowKeys));

                if (currentIndex > -1) {
                    const nextIndex = reverse ? currentIndex - 1 : currentIndex + 1;
                    const nextItem =
                        nextIndex < searchData.length && nextIndex >= 0
                            ? searchData[nextIndex]
                            : nextSearchData[currentIndex];

                    if (nextItem) {
                        routeItem = find(nextSearchData, pick(nextItem, activeRowKeys));
                    }
                }
            }

            routeItem = routeItem || first(nextSearchData);

            if (routeItem) {
                dispatch(
                    push(
                        `/${route}/${join(
                            map(activeRowKeys, (rowKey) => get(routeItem, rowKey, '')),
                            '/'
                        )}`,
                    ),
                );
            }
        }
    }
}

function storeRegistered(dispatch, getState, servicesDataKey, nextState) {
    const {formName, noFetchOnRegister, providerServiceDataKey, searchOnly} = servicesConfig[servicesDataKey];
    let result;

    if (formName) {
        result = new Promise((resolve) => {
            Promise.resolve(
                dispatch({
                    type: 'FORM_REGISTER',
                    formName,
                })
            ).then(() => {
                if (!noFetchOnRegister) {
                    fetchServicesData(dispatch, servicesDataKey, getState()).then(resolve);
                } else {
                    resolve();
                }
            });
        });
    } else if (providerServiceDataKey) {
        if (nextState.servicesSearchData[providerServiceDataKey]) {
            result = fetchDataForRegisteredConsumer(dispatch, servicesDataKey, nextState);
        } else {
            result = Promise.resolve(
                dispatch({
                    type: 'SEARCH_DATA_REGISTER_STORE',
                    servicesDataKey: providerServiceDataKey,
                })
            );
        }
    } else if (searchOnly) {
        const lastUpdated = get(nextState, ['servicesSearchData', servicesDataKey, 'lastUpdated']);

        if (!lastUpdated || lastUpdated < now() - 10000) {
            result = fetchServicesData(dispatch, servicesDataKey, nextState);
        } else {
            result = Promise.resolve('Value Good');
        }
    } else {
        result = fetchServicesData(dispatch, servicesDataKey, nextState);
    }

    return result;
}

function fetchDataForConsumers(dispatch, servicesDataKey, nextState) {
    let results = [];
    const {consumerServiceDataKeys} = servicesConfig[servicesDataKey];

    if (consumerServiceDataKeys) {
        results = map(consumerServiceDataKeys, (consumerServiceDataKey) =>
            fetchDataForRegisteredConsumer(dispatch, consumerServiceDataKey, nextState)
        );
    }

    return Promise.all(results);
}

function fetchDataForSearch(dispatch, servicesDataKey) {
    let results = [];
    const {searchServiceDataKeys} = servicesConfig[servicesDataKey];

    if (searchServiceDataKeys) {
        results = map(searchServiceDataKeys, (searchServiceDataKey) =>
            Promise.resolve(
                dispatch({
                    type: 'SEARCH_DATA_REGISTER_STORE',
                    servicesDataKey: searchServiceDataKey,
                })
            ),
        );
    }

    return Promise.all(results);
}

function fetchDataForRegisteredConsumer(dispatch, consumerServiceDataKey, nextState) {
    if (nextState.servicesSearchData[consumerServiceDataKey]) {
        const searchQuery = createSearchQueryForConsumer(consumerServiceDataKey, nextState);

        if (searchQuery) {
            return fetchData(dispatch, consumerServiceDataKey, searchQuery);
        }
    }
}

function createSearchQueryForConsumer(consumerServiceDataKey, nextState) {
    const {providerSearchKeys, providerServiceDataKey} = servicesConfig[consumerServiceDataKey];

    if (!isEmpty(providerSearchKeys) && providerServiceDataKey) {
        const searchQuery = {query: {rsql: {}}};

        map(providerSearchKeys, ({queryKey, recordKey, searchType}) => {
            const recordValues = getProviderRecordValues(recordKey, providerServiceDataKey, nextState);

            if (searchType === 'path') {
                searchQuery.path = searchQuery.path ? [searchQuery.path, recordValues].join('/') : recordValues;
            } else if (searchType === 'rsqlQuery') {
                searchQuery.query.rsql[queryKey] = recordValues || -1;
            }
        });

        return searchQuery;
    }

    return false;
}

function getProviderRecordValues(providerSearchKey, providerServiceDataKey, nextState) {
    const providerRecord = get(nextState.servicesSearchData, [providerServiceDataKey, 'searchData', 'content', 0], {});

    return includes(providerSearchKey, '||')
        ? join(compact(providerSearchKey.split('||').map((searchKey) => get(providerRecord, searchKey))), '||')
        : get(providerRecord, providerSearchKey, '');
}

function handleRouterUpdate(dispatch, routerParams, state, nextState) {
    let results = [];
    const {router: {routerParams: oldRouterParams} = {}} = state;

    if (oldRouterParams && routerParams) {
        const changedRouterKeys = Object.keys(pickBy(routerParams, (value, key) => oldRouterParams[key] !== value));
        const servicesDataKeys = Object.keys(nextState.servicesSearchData);

        results = map(servicesDataKeys, (servicesDataKey) => {
            const configRouterKeys = map(get(servicesConfig[servicesDataKey], 'routerKeys', []), 'routerKey');

            if (intersection(configRouterKeys, changedRouterKeys).length) {
                return fetchServicesData(dispatch, servicesDataKey, nextState);
            }
        });
    }

    return Promise.all(results);
}

function refreshServicesData(dispatch, servicesDataKey, nextState, selectRecord) {
    const providerServiceDataKey = get(servicesConfig, [servicesDataKey, 'providerServiceDataKey']);

    return fetchServicesData(dispatch, providerServiceDataKey || servicesDataKey, nextState, selectRecord);
}

function resetServicesData(dispatch) {
    return new Promise((resolve) => {
        Promise.resolve(
            dispatch({
                type: 'SEARCH_DATA_RESET',
                resetStore: true,
            })
        ).then(resolve);
    });
}

function fetchServicesData(dispatch, servicesDataKey, nextState, selectRecord) {
    return fetchData(
        dispatch,
        servicesDataKey,
        getStoreQuery(servicesDataKey, nextState),
        get(nextState, 'router.routerParams'),
        selectRecord
    );
}

function shouldFetchData(servicesDataKey, path, query) {
    const {routerKeys} = servicesConfig[servicesDataKey];
    let result;

    if (!routerKeys) {
        result = true;
    } else {
        map(routerKeys, ({queryKey, routerKey, searchType}) => {
          result = result || (searchType === 'path' && !isEmpty(path)) ||
            (searchType === 'rsqlQuery' && (get(query.rsql, queryKey) || get(query.rsql, routerKey)));
        });
    }

    return result;
}

function getCancelToken(servicesDataKey) {
    if (currentCancelTokens[servicesDataKey]) {
        currentCancelTokens[servicesDataKey].cancel();
    }

    currentCancelTokens[servicesDataKey] = generateCancelToken();

    return currentCancelTokens[servicesDataKey].token;
}

function fetchData(dispatch, servicesDataKey, {path, query}, routerParams, selectRecord = false) {
    if (!query) {
        return Promise.reject();
    }

    if (
        (servicesDataKey.includes('moviesAndPrograms') || servicesDataKey.includes('bestMatches')) &&
        query &&
        query.rsql
    ) {
        query.rsql.retired = 'false';
    }

    if (shouldFetchData(servicesDataKey, path, query)) {
        const dispatchFetched = (jsonResponse) => {
            if (servicesDataKey === 'moviesAndPrograms') {
                jsonResponse?.content?.forEach((r) => {
                    r.roviProgramId = r.roviProgramId?.toString() === '0' ? null : r.roviProgramId;
                });
            }
            dispatch({
                type: 'SEARCH_DATA_FETCH_FULFILLED',
                path,
                query,
                searchData: jsonResponse,
                servicesDataKey,
                selectRecord: selectRecord || isEmpty(routerParams),
            });
        };
        const dispatchError = (error) => {
            dispatch({
                type: 'SEARCH_DATA_FETCH_REJECTED',
                error,
                servicesDataKey,
            });
        };
        const dispatchCancel = () => {
            dispatch({
                type: 'SEARCH_DATA_FETCH_CANCELED',
                servicesDataKey,
            });
        };
        const config = servicesConfig[servicesDataKey];
        const actionParams = {
            cancelToken: getCancelToken(servicesDataKey),
            dispatchCancel,
            dispatchFetched,
            dispatchError,
            servicesConfig: config,
        };

        dispatch({
            type: 'SEARCH_DATA_FETCH',
            servicesDataKey,
        });

        return actionsServiceCaller(actionParams, getQueryParams(query, config), path);
    }
    return Promise.resolve(
        dispatch({
            type: 'SEARCH_DATA_RESET',
            servicesDataKey,
        })
    );
}

export function getStoreQuery(servicesDataKey, {formData, router, servicesSearchData}) {
    const {defaultSort, formName, routerKeys, rsqlQuery} = servicesConfig[servicesDataKey];
    const serviceFormData = formName ? get(formData, [formName], {}) : {};
    const routerParams = getRouterParams(routerKeys, get(router, 'routerParams', {}));
    const servicesSearchQuery = get(servicesSearchData, [servicesDataKey, 'query'], {});
    const rsql = {
        ...serviceFormData.values,
        ...routerParams.rsqlQuery,
        ...rsqlQuery,
    };

    return {
        path: routerParams.path,
        query: {
            ...cloneDeep(servicesSearchQuery),
            defaultSort,
            ...pick(rsql, [...rsqlConfig.extractFields, rsqlConfig.unexpiredOnlyFields]),
            rsql: omit(rsql, [...rsqlConfig.extractFields, rsqlConfig.unexpiredOnlyFields]),
            operators: serviceFormData.operators,
            ...getQueryFromProviderForm(servicesDataKey, formData),
        },
    };
}

function getQueryFromProviderForm(servicesDataKey, formData) {
    const {providerFormName, providerFormFields} = servicesConfig[servicesDataKey];
    return reduce(
        providerFormFields,
        (obj, {fieldName, queryKey}) => {
            set(obj, queryKey, get(get(formData, [providerFormName, 'values'], {}), fieldName));
            return obj;
        },
        {}
    );
}

export function getRouterParams(routerKeys, params) {
    const routerParams = {rsqlQuery: {}};

    map(routerKeys, (routerConfig) => {
        const {defaultValue, operator, queryKey, routerKey, searchType} = routerConfig;
        const routerValue = params[routerKey] || defaultValue;

        if (routerValue) {
            if (searchType === 'path') {
                routerParams.path = routerParams.path ? [routerParams.path, routerValue].join('/') : routerValue;
            } else if (searchType === 'rsqlQuery') {
                const valueOperator = operator || '';

                routerParams.rsqlQuery[queryKey || routerKey] = valueOperator + routerValue;
            }
        }
    });

    return routerParams;
}

export function getQueryParams(storeQuery, config) {
    const {defaultSort, exactSearch, operators, page, pageLimit, rsql, sort, timeout, unexpiredOnly} = storeQuery;
    const rsqlQuery = rsqlGenerator(rsql, operators, config);
    const sortQuery = sortGenerator(sort, defaultSort);
    const query = {
        page: page > 1 ? page - 1 : undefined,
        size: pageLimit !== 20 ? pageLimit : undefined,
        timeout,
    };

    if (unexpiredOnly) {
        query.activeOnly = true;
    }

    if (exactSearch) {
        query.exactSearch = true;
    }

    if (isArray(sortQuery) && sortQuery.length) {
        set(query, 'sort', sortQuery);
    }

    map(config.providerFormFields, ({queryKey}) => {
        set(query, queryKey, get(storeQuery, queryKey));
    });

    const ret = rsqlQuery
        ? config.queryType === 'queryParams'
            ? {
                  ...query,
                  ...rsql,
              }
            : {
                  ...query,
                  q: rsqlQuery,
              }
        : query;

    if (config.exclude_from_rsql && isArray(config.exclude_from_rsql) && config.exclude_from_rsql.length) {
        config.exclude_from_rsql.map((key) => {
            if (config.paramKey) {
                ret[config.paramKey[key]] = get(rsql, key);
            }
        });
    }
    return ret;
}

function rsqlGenerator(rsql, operators, config) {
    let rsqlQuery = [];
    let repeatedRsqlQueries = [];
    const addedColonAndNumberRegex = /:[0-9]/g;

    const repeatedKeys = Object.keys(operators || {}).filter((key) => addedColonAndNumberRegex.test(key));

    if (rsql && repeatedKeys.length < 1) {
        rsqlQuery = plainObjectFlattener({rsql, operators, config});
    } else if (rsql) {
        const repeatedRsqlQueriesObject = {};

        const nonRepeatedRsqlQuery = omit(rsql, repeatedKeys);
        rsqlQuery = plainObjectFlattener({rsql: nonRepeatedRsqlQuery, operators, config});

        const operatorsForRepeateKeys = {};

        repeatedKeys.forEach((key) => {
            const keyWitoutSpecialChars = key.replace(addedColonAndNumberRegex, '');

            repeatedRsqlQueriesObject[keyWitoutSpecialChars] = rsql[key];

            operatorsForRepeateKeys[keyWitoutSpecialChars] = operators[key];
        });

        repeatedRsqlQueries = plainObjectFlattener({
            rsql: repeatedRsqlQueriesObject,
            operators: operatorsForRepeateKeys,
            config,
        });
    }

    return [...rsqlQuery, ...repeatedRsqlQueries].join(';');
}

function escapeRsqlValue(value) {
    return isString(value) ? value.replace(/["]/g, '\\"') : value;
}

function getOperatorGroup(value, keyPath, operator) {
    let argument;

    if (isArray(value)) {
        argument = value.length
            ? `("${map(value, (rsqlValue) => escapeRsqlValue(rsqlValue)).join('","')}")`
            : undefined;
    } else {
        argument = escapeRsqlValue(value);
    }

    return argument ? `${keyPath}${operator}${argument}` : undefined;
}

function getCriteriaGroup(value, keyPath) {
    let comparisons;
    let rsql;

    if (includes(keyPath, '&&')) {
        comparisons = uniq(map(keyPath.split('&&'), (key) => getCriteria(value, key)));
        rsql = comparisons.join(';');
    } else {
        comparisons = uniq(map(keyPath.split('||'), (key) => getCriteria(value, key)));
        rsql = comparisons.join(',');
        rsql = comparisons.length > 1 ? `(${rsql})` : rsql;
    }

    return rsql;
}

function getCriteria(unescapedValue, keyPath) {
    const value = escapeRsqlValue(unescapedValue);
    let operator;
    let argument;

    if (value === 'isNull') {
        operator = '=isNull=';
        argument = 'true';
    } else if (value === 'notNull') {
        operator = '=notNull=';
        argument = 'true';
    } else if (/^!/.test(value)) {
        operator = '!=';
        argument = `"${value.substr(1)}"`;
    } else if (/^>=/.test(value)) {
        operator = '=ge=';
        argument = `"${value.substr(2)}"`;
    } else if (/^>/.test(value)) {
        operator = '=gt=';
        argument = `"${value.substr(1)}"`;
    } else if (/^<=/.test(value)) {
        operator = '=le=';
        argument = `"${value.substr(2)}"`;
    } else if (/^</.test(value)) {
        operator = '=lt=';
        argument = `"${value.substr(1)}"`;
    } else if (isArray(value)) {
        operator = '=in=';
        argument = `("${value.join('","')}")`;
    } else if (includes(value, '||')) {
        operator = '=in=';
        argument = `("${value.split('||').join('","')}")`;
    } else if (/^=(not_in|out)=.+/.test(value)) {
        operator = '=out=';
        argument = unescapedValue.replace(/^=(not_in|out)=/, '');
    } else if (/^=rsql=.+/.test(value)) {
        return `${unescapedValue.substr(6)}`;
    } else if (/^=between=\(.+\)$/.test(value)) {
        operator = '=between=';
        argument = value.substr(9);
    } else {
        operator = '==';
        argument =
            includes(rsqlConfig.textFields, keyPath) && includes(['true', 'false'], value)
                ? `"\\"${value}\\""`
                : `"${value}"`;
    }

    return `${first(keyPath.split('#'))}${operator}${argument}`;
}

function plainObjectFlattener({keyPath, operators, query, rsql, value, config}) {
    const currentValue = value === false || value ? value : rsql;
    const currentQuery = query || [];
    const exclude = typeof config !== 'undefined' && config.exclude_from_rsql && isArray(config.exclude_from_rsql) && config.exclude_from_rsql.length
      ? config.exclude_from_rsql
      : [];

    if (isPlainObject(currentValue)) {
        Object.keys(currentValue).forEach((key) => {
            let newKeypath = keyPath ? `${keyPath}.${key}` : key;
            if (!exclude.includes(newKeypath)) {
                if (key === 'dateRange') {
                    let dateFrom = get(currentValue, 'dateRange.from');
                    let dateTo = get(currentValue, 'dateRange.to');

                    if (dateFrom) {
                        dateFrom = moment(dateFrom.includes('T') ? dateFrom : `${dateFrom} 00:00:00`).format();
                    }

                    if (dateTo) {
                        dateTo = moment(dateTo.includes('T') ? dateTo : `${dateTo} 23:59:59`).format();
                    }

                    newKeypath = newKeypath.split('.dateRange').join('');

                    if (dateFrom && dateTo) {
                        plainObjectFlattener({
                            value: `=between=(${dateFrom},${dateTo})`,
                            keyPath: newKeypath,
                            query: currentQuery,
                            config,
                        });
                    } else if (dateFrom) {
                        plainObjectFlattener({value: `>${dateFrom}`, keyPath: newKeypath, query: currentQuery, config});
                    } else if (dateTo) {
                        plainObjectFlattener({value: `<=${dateTo}`, keyPath: newKeypath, query: currentQuery, config});
                    }
                } else {
                    plainObjectFlattener({
                        value: currentValue[key],
                        keyPath: newKeypath,
                        query: currentQuery,
                        operators: get(operators, key),
                        config,
                    });
                }
            }
        });
    } else if (
        (currentValue || currentValue === false) &&
        currentValue !== '' &&
        !(isArray(currentValue) && isEmpty(currentValue))
    ) {
        if (operators) {
            currentQuery.push(getOperatorGroup(currentValue, keyPath, operators));
        } else if (!/^#/.test(keyPath)) {
            currentQuery.push(getCriteriaGroup(currentValue, keyPath));
        }
    }

    return currentQuery;
}

function sortGenerator(sort, defaultSort) {
    const sortQuery = map(sort, ({column, order}) => [column, order].join(','));
    const addDefaultSort = (defaultSort) => {
        if (defaultSort && defaultSort.length === 2 && !find(sort, ['column', defaultSort[0]])) {
            sortQuery.push(defaultSort.join(','));
        }
    };

    if (defaultSort && defaultSort.length) {
        addDefaultSort(['_score', 'desc']);

        if (isArray(defaultSort[0])) {
            map(defaultSort, addDefaultSort);
        } else {
            addDefaultSort(defaultSort);
        }
    }

    return sortQuery;
}

export default fetchMiddleware;
